import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Copy, ArrowLeft, ArrowRight, DollarSign } from 'lucide-react';
import Layout from '../components/Layout';
import incomeService from '../services/incomeService';
import IncomeForm from '../components/IncomeForm';
import transactionService from '../services/transactionService';

export default function IncomesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<any | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [filterMonth, setFilterMonth] = React.useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = React.useState(new Date().getFullYear());

  const { data = [], isLoading } = useQuery({
    queryKey: ['incomes', filterMonth, filterYear],
    queryFn: () => incomeService.list(filterMonth, filterYear),
  });

  const createMutation = useMutation({
    mutationFn: (payload: any) => incomeService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incomes', filterMonth, filterYear] });
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Fonte de renda criada com sucesso!' } }));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: any) => incomeService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incomes', filterMonth, filterYear] });
      setEditing(null);
      setShowForm(false);
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Fonte de renda atualizada com sucesso!' } }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => incomeService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incomes', filterMonth, filterYear] });
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Fonte de renda excluída com sucesso!' } }));
    },
  });

  const onSubmit = async (payload: any) => {
    if (editing?.id) {
      await updateMutation.mutateAsync({ id: editing.id, payload });
    } else {
      await createMutation.mutateAsync(payload);

      const txMonth = payload.month || filterMonth;
      const txYear = payload.year || filterYear;
      // Use UTC noon to evitar fuso mudar o mês (ex.: -3h cair no mês anterior)
      const dueDate = new Date(Date.UTC(txYear, txMonth - 1, 15, 12)).toISOString();
      try {
        await transactionService.create({
          type: 'credit',
          amount: payload.amount,
          description: payload.name || payload.description || 'Renda',
          category: 'Renda',
          dueDate,
          isPaid: true,
          month: txMonth,
          year: txYear,
        });
        qc.invalidateQueries({ queryKey: ['transactions'] });
      } catch (err) {
        console.error('Falha ao criar transação da renda', err);
      }
    }
  };

  const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  const total = data.reduce((sum: number, inc: any) => sum + (inc.amount || 0), 0);

  const fmt = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const goPrev = () => {
    if (filterMonth === 1) {
      setFilterMonth(12);
      setFilterYear(filterYear - 1);
    } else {
      setFilterMonth(filterMonth - 1);
    }
  };

  const goNext = () => {
    if (filterMonth === 12) {
      setFilterMonth(1);
      setFilterYear(filterYear + 1);
    } else {
      setFilterMonth(filterMonth + 1);
    }
  };

  return (
    <Layout title="Fontes de Renda" icon={DollarSign}>
      <div className="flex flex-col h-full bg-gray-100">
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Fontes de Renda</h1>
                  <p className="text-gray-600 text-sm mt-1">Gerencie suas fontes de renda (salário, vale alimentação, etc)</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="flex justify-start mb-2 md:mb-4">
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 font-medium w-full md:w-auto text-sm"
            >
              + Nova Fonte de Renda
            </button>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <button onClick={goPrev} className="h-10 w-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-900 bg-white" aria-label="Mês anterior">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(Number(e.target.value))}
                className="flex-1 border border-gray-200 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 appearance-none text-gray-900 bg-white capitalize"
              >
                {monthNames.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(Number(e.target.value))}
                className="border border-gray-200 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 w-28 appearance-none text-gray-900 bg-white"
              >
                {[2023, 2024, 2025, 2026].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button onClick={goNext} className="h-10 w-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-900 bg-white" aria-label="Próximo mês">
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="bg-green-50 border border-green-100 rounded-lg p-4">
            <p className="text-xs md:text-sm text-gray-600">Total de Renda</p>
            <p className="text-base md:text-2xl font-bold text-green-600">{fmt(total)}</p>
          </div>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-md rounded-lg shadow-lg border border-gray-200">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">{editing ? 'Editar Fonte de Renda' : 'Nova Fonte de Renda'}</h3>
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-500 hover:text-gray-700" aria-label="Fechar">✕</button>
              </div>
              <div className="p-6">
                <IncomeForm
                  initial={editing ?? undefined}
                  onCancel={() => { setShowForm(false); setEditing(null); }}
                  onSubmit={onSubmit}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          {isLoading ? (
            <div className="text-center py-10 text-gray-500">Carregando...</div>
          ) : data.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.map((inc: any) => (
                <div key={inc.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center text-green-700">
                      <DollarSign className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900">{inc.name || inc.description}</p>
                      <p className="text-sm text-gray-600 capitalize">{monthNames[(inc.month ?? filterMonth) - 1]} / {inc.year}</p>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <button
                        className="p-2 rounded hover:bg-gray-100"
                        title="Duplicar"
                        onClick={() => {
                          const baseMonth = inc.month ?? filterMonth;
                          const baseYear = inc.year ?? filterYear;
                          const nextMonth = baseMonth === 12 ? 1 : baseMonth + 1;
                          const nextYear = baseMonth === 12 ? baseYear + 1 : baseYear;
                          const clone = { ...inc, id: undefined, month: nextMonth, year: nextYear };
                          setEditing(clone);
                          setShowForm(true);
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 rounded hover:bg-gray-100 text-blue-600"
                        title="Editar"
                        onClick={() => { setEditing(inc); setShowForm(true); }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 rounded hover:bg-gray-100 text-red-600"
                        title="Excluir"
                        onClick={() => {
                          if (confirm('Tem certeza que deseja excluir esta renda?')) deleteMutation.mutate(inc.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{fmt(inc.amount)}</p>
                  {inc.description ? <p className="text-sm text-gray-600">{inc.description}</p> : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">Nenhuma fonte de renda encontrada</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
